import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPencil,
    faTrash,
    faUsersSlash,
    faUsersGear,
    faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { Button, ConfirmModal, Modal, Toggle } from '@quyva/shared-ui';
import { Table } from 'antd';
import { LoadingView } from '@quyva/shared-ui';
import EditAgentView from './editAgentView';
import axios from 'axios';
import { getUrl } from '../pdfDisplay/utils';
import { alert, AlertType } from '@quyva/shared-ui';
import { getDate, getTime } from '@quyva/common';

const truncateWords = (text, wordLimit) => {
    if (!text) return '';
    const words = text.split(' ');
    return words.length > wordLimit
        ? words.slice(0, wordLimit).join(' ') + '...'
        : text;
};

const AgentMainView = ({ user, itemsPerPage, agentList, getAgentList, updateAgentInList  }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [editAgent, setEditAgent] = useState(null);
    const [deleteAgent, setDeleteAgent] = useState(null);

    const handleAgentDelete = async () => {
        if (!deleteAgent) return;
        try {
            setIsLoading(true);
            setLoadingText(`Deleting ${deleteAgent.name}...`);
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/agent_manager/delete_agent/${deleteAgent.id}`
            );
            const encodedToken = encodeURIComponent(user.token);
            await axios.delete(dynamicUrl, {
                headers: { Authorization: encodedToken },
            });
            alert(AlertType.SUCCESS, 'Agent deleted successfully.');
            setDeleteAgent(null);
            await getAgentList();
        } catch (error) {
            alert(AlertType.ERROR, 'Failed to delete agent.');
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    const handleDownloadAgentScript = async (record) => {
        setIsLoading(true);
        setLoadingText(`Preparing to download ${record.name}.py...`);
        try {
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/agent_manager/download_agent_python_script?agent_name=${record.name}`
            );
            const encodedToken = encodeURIComponent(user.token);
            const response = await axios.get(dynamicUrl, {
                responseType: 'blob',
                headers: {
                    Authorization: encodedToken,
                    'user-email': user.email,
                },
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `${record.name}.py`;
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            alert(
                AlertType.SUCCESS,
                `${record.name}.py downloaded successfully.`
            );
        } catch (error) {
            console.error('Download failed:', error);
            alert(AlertType.ERROR, 'Failed to download agent script.');
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    const updateAsyncExecution = async (agentId, isAsyncEnabled) => {
        updateAgentInList(agentId, { asyncExecution: isAsyncEnabled });
        try {
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/agent_manager/update_agent_async_mode`
            );

            const formData = new FormData();
            formData.append('agentId', agentId);
            formData.append('asyncExecution', isAsyncEnabled);

            const encodedToken = encodeURIComponent(user.token);

            const response = await axios.post(dynamicUrl, formData, {
                headers: {
                    Authorization: encodedToken,
                },
            });

            alert(
                AlertType.SUCCESS,
                response?.data?.message || 'Updated successfully'
            );
        } catch (err) {
            updateAgentInList(agentId, { asyncExecution: !isAsyncEnabled });
            alert(
                AlertType.ERROR,
                err?.response?.data?.detail || 'Failed to update execution mode'
            );
        }
    };

    const NoAgentsView = () => (
        <div className='flex flex-col w-full h-[78vh] items-center justify-center text-texts-secondary300'>
            <FontAwesomeIcon
                icon={faUsersSlash}
                className='w-[8vw] h-[8vw] mb-4 text-icons-primary'
            />
            <h1 className='text-2xl font-semibold'>No Agents Found</h1>
            <p className='text-lg mt-2'>
                There are currently no agents to display.
            </p>
        </div>
    );

    const columns = [
        { title: 'AGENT NAME', dataIndex: 'name', key: 'name' },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <span title={text}>{truncateWords(text, 4)}</span>
            ),
        },
        {
            title: 'CREATED AT',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => (
                <>
                    <span className='mr-2'>{getDate(text)}</span>
                    <span>{getTime(text)}</span>
                </>
            ),
        },
        {
            title: 'ACTIONS',
            key: 'actions',
            render: (_, record) => (
                <div className='flex items-center justify-center gap-2'>
                    <Button
                        icon={faPencil}
                        onClick={() => setEditAgent(record)}
                        hoverText={'Edit Agent'}
                        type={'warning'}
                    />
                    <Button
                        icon={faTrash}
                        onClick={() => setDeleteAgent(record)}
                        hoverText={'Delete Agent'}
                        type={'danger'}
                    />
                    <Button
                        icon={faDownload}
                        onClick={() => handleDownloadAgentScript(record)}
                        hoverText={'Download Agent Script'}
                        type={'info'}
                    />
                </div>
            ),
        },
        {
            title: 'BACKGROUND TASK',
            key: 'background_task',
            render: (_, record) => (
                <Toggle
                    checked={
                        record.has_long_running_tool
                            ? true
                            : record.asyncExecution
                    }
                    disabled={record.has_long_running_tool}
                    onChange={(checked) => {
                        updateAsyncExecution(record.id, checked);
                    }}
                    title='Run this agent in background'
                />
            ),
        },
    ];

    if (isLoading && !editAgent && !deleteAgent) {
        return (
            <div className='w-full h-full flex justify-center items-center'>
                <LoadingView loadingText={loadingText} inLine={false} />
            </div>
        );
    }

    return (
        <div className='p-4'>
            {agentList.length === 0 ? (
                <NoAgentsView />
            ) : (
                <Table
                    dataSource={agentList}
                    columns={columns}
                    rowKey='id'
                    pagination={{
                        pageSize: itemsPerPage,
                        total: agentList.length,
                        showSizeChanger: false,
                    }}
                />
            )}
            {editAgent && (
                <Modal
                    title={`Edit Agent: ${editAgent.name}`}
                    titleIcon={faUsersGear}
                    onClose={() => setEditAgent(null)}
                >
                    <EditAgentView
                        agentData={editAgent}
                        setEditMode={setEditAgent}
                        user={user}
                        agentList={agentList}
                        getAgentList={getAgentList}
                    />
                </Modal>
            )}
            {deleteAgent && (
                <ConfirmModal
                    modalText={`Are you sure you want to delete "${deleteAgent.name}"?`}
                    onConfirm={handleAgentDelete}
                    onCancel={() => setDeleteAgent(null)}
                />
            )}
        </div>
    );
};

export default AgentMainView;
